"use client";
import { useCallback, useEffect, useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"

// --- Hooks ---
import { useTiptapEditor } from "hooks/use-tiptap-editor"
import { useIsBreakpoint } from "hooks/use-is-breakpoint"

// --- Lib ---
import { isExtensionAvailable } from "lib/tiptap-utils"

// --- Icons ---
import { ImagePlusIcon } from "components/tiptap-icons/image-plus-icon"


/**
 * Checks if image can be inserted in the current editor state
 */
export function canInsertImageCarousel(editor) {
  if (!editor || !editor.isEditable) return false

  return editor.can().insertContent({ type: "imageCarousel" });
}

/**
 * Checks if image is currently active
 */
export function isImageActive(editor) {
  if (!editor || !editor.isEditable) return false
  return editor.isActive("imageCarousel");
}

/**
 * Inserts an image in the editor
 */
export function insertImageCarousel(editor) {
  if (!canInsertImageCarousel(editor)) return false

  editor.commands.insertCarousel([]);
}

/**
 * Determines if the image button should be shown
 */
export function shouldShowButton(props) {
  const { editor, hideWhenUnavailable } = props

  if (!editor || !editor.isEditable) return false

  if (hideWhenUnavailable && !editor.isActive("code")) {
    return canInsertImageCarousel(editor);
  }

  return true
}

export function useImageCarousel(config) {
  const {
    editor: providedEditor,
    hideWhenUnavailable = false,
    onInserted,
  } = config || {}

  const { editor } = useTiptapEditor(providedEditor)
  const isMobile = useIsBreakpoint()
  const [isVisible, setIsVisible] = useState(true)
  const canInsert = canInsertImageCarousel(editor)
  const isActive = isImageActive(editor)

  useEffect(() => {
    if (!editor) return

    const handleSelectionUpdate = () => {
      setIsVisible(shouldShowButton({ editor, hideWhenUnavailable }))
    }

    handleSelectionUpdate()

    editor.on("selectionUpdate", handleSelectionUpdate)

    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate)
    };
  }, [editor, hideWhenUnavailable])

  const handleImageCarousel = useCallback(() => {
    if (!editor) return false

    const success = insertImageCarousel(editor)
    if (success) {
      onInserted?.()
    }
    return success
  }, [editor, onInserted])

  return {
    isVisible,
    isActive,
    handleImageCarousel,
    canInsert,
    label: "Add image carousel",
    Icon: ImagePlusIcon,
  }
}
